// src/components/services/FormAddEditService.jsx

import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Grid,
    Stack,
    Button,
    useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import useCreateService from "../../hooks/services/useCreateService";
import useUpdateService from "../../hooks/services/useUpdateService";
import { useUploadMedia } from "../../hooks/media/useUploadMedia";

import FormInput from "../inputs/FormInput";
import i18next from "i18next";
import LanguageToggle from "../global/LanguageToggle";
import useSingleImageField from "../../hooks/media/useSingleImageField";

const FormAddEditService = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const initialLang =
        localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(
        initialLang.startsWith("ar") ? "ar" : "en"
    );

    const showArabic = activeLang === "ar";
    const showEnglish = activeLang === "en";

    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [status, setStatus] = useState("active");

    const { createServiceMutate, isPending: isCreating } = useCreateService();
    const { editService, isUpdate } = useUpdateService();
    const { uploadMedia, isPending: isUploading } = useUploadMedia();

    const img = useSingleImageField({
        initialImage: null, // هنزامنها يدويًا في useEffect
        uploadMedia,
        isArabic,
    });

    const loadingSubmit = isCreating || isUpdate;

    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setStatus(currentData?.status || "active");

            img.setFromServerImage(currentData?.image || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, currentData]);

    const SubmitForm = (e) => {
        e.preventDefault();

        const payload = {
            name: {
                ar: nameAr || nameEn,
                en: nameEn || nameAr,
            },
            status,

            // ✅ لو مفيش صورة => لا يوجد key image أصلًا
            ...img.getImagePayload(),
        };

        if (isEdit && currentData?.id) {
            editService(
                { id: currentData.id, req: payload },
                { onSuccess: () => onSubmitSuccess?.() }
            );
        } else {
            createServiceMutate(payload, {
                onSuccess: () => onSubmitSuccess?.(),
            });
        }
    };

    const statusOptions = [
        { id: "active", name: isArabic ? "نشط" : "Active" },
        { id: "disabled", name: isArabic ? "معطل" : "Disabled" },
    ];

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box
                component="form"
                onSubmit={SubmitForm}
                sx={{
                    p: { xs: 2, md: 4 },
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Typography variant="h6">
                        {isEdit
                            ? isArabic
                                ? "تعديل خدمة"
                                : "Edit Service"
                            : isArabic
                                ? "إضافة خدمة جديدة"
                                : "Create New Service"}
                    </Typography>

                    <LanguageToggle
                        activeLang={activeLang}
                        onChange={(lng) => setActiveLang(lng)}
                        showLabel={true}
                    />
                </Box>

                {/* Image Upload Section */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 3,
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <FormInput
                        required={false}
                        type="file"
                        name="image"
                        label={isArabic ? "صورة الخدمة" : "Service Image"}
                        labelImage={isArabic ? "اختر الصورة" : "Choose image"}
                        labelBioImage={
                            isArabic
                                ? "اسحب وأسقط الصورة هنا أو انقر للاختيار"
                                : "Drag & drop or click to upload"
                        }
                        value={img.imagePreview}
                        onChange={img.handleImageChange}
                        isUploading={isUploading}
                        errorMessage={img.imageError}
                        successMessage={
                            img.imageName && !img.imageError
                                ? isArabic
                                    ? "تم رفع الصورة بنجاح"
                                    : "Image uploaded successfully"
                                : ""
                        }
                        inputProps={{ accept: "image/*" }}
                        mediaId={img.imageId}
                        onDeleted={img.handleDeleted}
                    />
                </Box>

                <Box mb={3}>
                    <Grid container spacing={2}>
                        {showArabic && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="الاسم (عربي)"
                                    placeholder="أدخل اسم الخدمة بالعربي"
                                    type="text"
                                    name="name_ar"
                                    value={nameAr}
                                    onChange={(e) => setNameAr(e.target.value)}
                                    required
                                />
                            </Grid>
                        )}

                        {showEnglish && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="Name (English)"
                                    placeholder="Enter service name in English"
                                    type="text"
                                    name="name_en"
                                    value={nameEn}
                                    onChange={(e) => setNameEn(e.target.value)}
                                    required
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "الحالة" : "Status"}
                                type="select"
                                name="status"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                options={statusOptions}
                                required
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Stack direction="row" spacing={1}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loadingSubmit || isUploading}
                        >
                            {loadingSubmit
                                ? isArabic
                                    ? "جارٍ الحفظ..."
                                    : "Saving..."
                                : isEdit
                                    ? isArabic
                                        ? "تحديث"
                                        : "Update"
                                    : isArabic
                                        ? "حفظ"
                                        : "Save"}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditService;
